import React, { createContext, useContext, ReactNode } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { UserGlobalSettings } from 'api/users/schemas/GlobalSettings';
import { useQuery, useQueryClient } from 'react-query';
import { getUserGlobalSettings } from 'api/users/api';

type Ctx = {
  settings?: UserGlobalSettings;
  isLoading: boolean;
  refetch: () => void;
  invalidate: () => void;
};

const UserGlobalSettingsContext = createContext<Ctx | undefined>(undefined);

export const UserGlobalSettingsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { authState } = useOktaAuth();

  const g360 = (() => {
    try {
      return JSON.parse(localStorage.getItem('g360-token-storage') ?? '{}');
    } catch {
      return undefined;
    }
  })();

  const enabled =
    !!authState?.isAuthenticated &&
    !!g360?.token &&
    typeof g360?.user_id === 'string';

  const queryClient = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['global_settings'],
    queryFn: getUserGlobalSettings,
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['global_settings'] });

  return (
    <UserGlobalSettingsContext.Provider
      value={{
        settings: data,
        isLoading: !enabled || isLoading,
        refetch,
        invalidate,
      }}
    >
      {children}
    </UserGlobalSettingsContext.Provider>
  );
};

export const useUserGlobalSettings = () => {
  const ctx = useContext(UserGlobalSettingsContext);
  if (!ctx)
    throw new Error(
      'useUserGlobalSettings must be used within UserGlobalSettingsProvider',
    );
  return ctx;
};
